import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LockIcon from "@mui/icons-material/Lock";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { Box, Container, Typography } from "@mui/material";
import { Button } from "Components/_Shared/Buttons/Button";
import { HStack } from "Components/_Shared/Core/Stack";
import { DialogModal } from "Components/_Shared/DialogModal";
import type { AnalysisDetail } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type ReactNode, useState } from "react";
import { TakeBackConfirmDialog } from "./Sharing/TakeBackConfirmDialog";
import { useReturnEditAccess } from "./Sharing/useReturnEditAccess";
import { getAgentFullName } from "./Sharing/utils";
import { type AnalysisAccess, AnalysisAccessStatus } from "./useAnalysisAccess";

const CTA_HEIGHT = 36;

type Props = {
  readonly analysis: AnalysisDetail;
  readonly access: AnalysisAccess;
  readonly isCreatingNewVersion: boolean;
  readonly onCreateNewVersion: () => void;
};

type BarContent = {
  readonly title: string;
  readonly text: string;
  readonly action: ReactNode;
};

type ActionLinkProps = {
  readonly loading?: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
};

const ActionLink = ({ loading, onClick, children }: ActionLinkProps) => (
  <Button
    variant="link"
    loading={loading}
    onClick={onClick}
    // the negative margin cancels the Button's adornment gap, the chevron's own side bearing is gap enough
    endAdornment={<KeyboardArrowRightIcon fontSize="small" sx={{ ml: -Spacing.M }} />}
    sx={(theme) => ({
      height: CTA_HEIGHT,
      flexShrink: 0,
      "& .MuiSvgIcon-root": {
        transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shorter }),
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      },
      "&:hover .MuiSvgIcon-root, &.Mui-focusVisible .MuiSvgIcon-root": { transform: "translateX(3px)" },
    })}
  >
    {children}
  </Button>
);

export const AnalysisStatusBar = ({ analysis, access, isCreatingNewVersion, onCreateNewVersion }: Props) => {
  const { t } = useResource();
  const { returnAccess, isReturning } = useReturnEditAccess();
  const [isTakeBackConfirmOpen, setIsTakeBackConfirmOpen] = useState(false);
  const [isReturnConfirmOpen, setIsReturnConfirmOpen] = useState(false);

  const createNewVersionButton = access.canCreateNewVersion ? (
    <ActionLink loading={isCreatingNewVersion} onClick={onCreateNewVersion}>
      {t(Resources.AnalysisDetail.StatusBar.CreateNewVersion)}
    </ActionLink>
  ) : null;

  const getContent = (): BarContent | null => {
    switch (access.status) {
      case AnalysisAccessStatus.sharedWithOther:
        return {
          title: t(Resources.AnalysisDetail.StatusBar.SharedWithOther.Title, {
            advisorName: analysis.editingAgent ? getAgentFullName(analysis.editingAgent) : "",
            advisorId: analysis.editingAgent?.ssoId ?? "",
          }),
          text: t(Resources.AnalysisDetail.StatusBar.SharedWithOther.Text),
          action: access.isOwner ? (
            <ActionLink onClick={() => setIsTakeBackConfirmOpen(true)}>{t(Resources.AnalysisDetail.Sharing.TakeOver)}</ActionLink>
          ) : null,
        };
      case AnalysisAccessStatus.sharedWithMe:
        return {
          title: t(Resources.AnalysisDetail.StatusBar.SharedWithMe.Title, { ownerName: analysis.agentName }),
          text: access.isEditable
            ? t(Resources.AnalysisDetail.StatusBar.SharedWithMe.Text)
            : t(Resources.AnalysisDetail.StatusBar.SharedWithMe.ReadOnlyText),
          action: (
            <>
              <ActionLink onClick={() => setIsReturnConfirmOpen(true)}>{t(Resources.AnalysisDetail.StatusBar.ReturnToAdvisor)}</ActionLink>
              {createNewVersionButton}
            </>
          ),
        };
      case AnalysisAccessStatus.declined:
        return {
          title: t(Resources.AnalysisDetail.StatusBar.Declined.Title),
          text: t(Resources.AnalysisDetail.StatusBar.ReadOnlyText),
          action: createNewVersionButton,
        };
      case AnalysisAccessStatus.completed:
        return {
          title: t(Resources.AnalysisDetail.StatusBar.Completed.Title),
          text: t(Resources.AnalysisDetail.StatusBar.ReadOnlyText),
          action: createNewVersionButton,
        };
      default:
        return null;
    }
  };

  const content = getContent();

  if (!content) {
    return null;
  }

  const isSharedWithMe = access.status === AnalysisAccessStatus.sharedWithMe;

  return (
    <>
      <Box
        component="output"
        display="block"
        sx={(theme) => ({
          bgcolor: isSharedWithMe ? theme.colors.brandLight : theme.colors.textTertiary,
          borderBottom: `1px solid ${isSharedWithMe ? theme.colors.brandLightBorder : theme.colors.border}`,
        })}
      >
        <Container>
          <HStack minHeight={52} alignItems="center" spacing={Spacing.L}>
            {isSharedWithMe ? (
              <PeopleAltOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />
            ) : (
              <LockIcon sx={(theme) => ({ fontSize: 18, color: theme.colors.darkGrey })} />
            )}
            <Typography fontSize={14}>
              <Box component="span" fontWeight={700}>
                {content.title}
              </Box>
              {/* the follow-up sentence only tells the user what the action does, so it goes away with the action */}
              {content.action && ` ${content.text}`}
            </Typography>
            {content.action}
          </HStack>
        </Container>
      </Box>
      <TakeBackConfirmDialog
        isOpen={isTakeBackConfirmOpen}
        advisor={access.sharedWithAgent}
        onClose={() => setIsTakeBackConfirmOpen(false)}
      />
      <DialogModal
        size="sm"
        isOpen={isReturnConfirmOpen}
        onClose={() => setIsReturnConfirmOpen(false)}
        isPending={isReturning}
        closeLabel={t(Resources.Common.Cancel)}
        title={t(Resources.AnalysisDetail.Sharing.Confirm.Return.Title)}
        subtitle={t(Resources.AnalysisDetail.Sharing.Confirm.Return.Text, { ownerName: analysis.agentName })}
        primaryAction={
          <Button loading={isReturning} onClick={() => returnAccess({ onSettled: () => setIsReturnConfirmOpen(false) })}>
            {t(Resources.AnalysisDetail.StatusBar.ReturnToAdvisor)}
          </Button>
        }
      />
    </>
  );
};
